'use client';

/**
 * Frontend error logger — sends uncaught errors to backend /api/public/frontend-error.
 * Lightweight: batches errors, drops duplicates within a short window, never throws.
 */

import { resolveApiBase } from './api';

// Musí jít přes resolveApiBase() — relativní /api/ na frontend doméně nemá proxy
// a končilo by 404 (jsme na sevasoft.armorik.eu, backend je api.sevasoft.armorik.eu).
const endpoint = () => `${resolveApiBase()}/api/public/frontend-error`;
const DEDUP_WINDOW_MS = 5000;
const recentFingerprints = new Map<string, number>();

function fingerprint(message: string, file?: string, line?: number): string {
  return `${message}|${file ?? ''}|${line ?? ''}`;
}

function send(payload: Record<string, unknown>) {
  try {
    const fp = fingerprint(String(payload.message ?? ''), String(payload.file ?? ''), payload.line as number | undefined);
    const now = Date.now();
    const last = recentFingerprints.get(fp);
    if (last && now - last < DEDUP_WINDOW_MS) return; // dedup
    recentFingerprints.set(fp, now);
    // Prune old entries
    if (recentFingerprints.size > 50) {
      recentFingerprints.forEach((t, k) => {
        if (now - t > DEDUP_WINDOW_MS) recentFingerprints.delete(k);
      });
    }

    fetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    }).catch(() => { /* silent — never throw */ });
  } catch { /* silent */ }
}

/**
 * Install global error handlers. Call once in the root layout.
 */
export function installErrorLogger() {
  if (typeof window === 'undefined') return;

  const userAgent = navigator.userAgent;
  const environment = process.env.NODE_ENV || 'unknown';
  const releaseTag = process.env.NEXT_PUBLIC_RELEASE_TAG || undefined;

  // Uncaught errors
  window.addEventListener('error', (event) => {
    send({
      message: event.message || 'Uncaught error',
      stack: event.error?.stack,
      file: event.filename,
      line: event.lineno,
      col: event.colno,
      url: window.location.href,
      userAgent,
      environment,
      releaseTag,
      userEmail: (window as any).__userEmail || undefined,
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    send({
      message: reason?.message || 'Unhandled promise rejection',
      stack: reason?.stack,
      url: window.location.href,
      userAgent,
      environment,
      releaseTag,
      userEmail: (window as any).__userEmail || undefined,
    });
  });
}

const slowFetchFingerprints = new Map<string, number>();
const SLOW_FETCH_DEDUP_MS = 60_000;

/**
 * Log a slow SWR request (loading >8s, fired from SWRConfig.onLoadingSlow).
 * Reuses the frontend-error channel so admins see which endpoints/users are
 * affected in Admin → Frontend errors. Dedup: same URL once per minute.
 */
export function logSlowFetch(swrKey: unknown) {
  if (typeof window === 'undefined') return;
  const raw = typeof swrKey === 'string' ? swrKey : JSON.stringify(swrKey);
  const key = String(raw ?? '').slice(0, 500);
  const now = Date.now();
  const last = slowFetchFingerprints.get(key);
  if (last && now - last < SLOW_FETCH_DEDUP_MS) return;
  slowFetchFingerprints.set(key, now);
  if (slowFetchFingerprints.size > 100) {
    slowFetchFingerprints.forEach((t, k) => {
      if (now - t > SLOW_FETCH_DEDUP_MS) slowFetchFingerprints.delete(k);
    });
  }
  send({
    message: `Slow fetch (>8s): ${key}`,
    file: 'swr',
    url: window.location.href,
    userAgent: navigator.userAgent,
    environment: process.env.NODE_ENV || 'unknown',
    releaseTag: process.env.NEXT_PUBLIC_RELEASE_TAG || undefined,
    userEmail: (window as any).__userEmail || undefined,
  });
}

/**
 * Manually log an error (for try/catch blocks).
 */
export function logError(error: Error | string, context?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  send({
    message: typeof error === 'string' ? error : (error.message || 'Unknown error'),
    stack: typeof error === 'object' ? error.stack : undefined,
    url: window.location.href,
    userAgent: navigator.userAgent,
    environment: process.env.NODE_ENV || 'unknown',
    releaseTag: process.env.NEXT_PUBLIC_RELEASE_TAG || undefined,
    userEmail: (window as any).__userEmail || undefined,
    ...context,
  });
}
